import React, { useEffect, useState, useRef, useCallback } from 'react';
import { getActivityCalendar } from '../config/api';

// 共用的活动日历颜色配置
export const ACTIVITY_CALENDAR_COLORS = {
  light: [
    '#edf2f7', // 0 - 适中的灰色（亮色主题）
    '#9be9a8', // 1-5小时 - 浅绿色
    '#40c463', // 6-11小时 - 中浅绿色
    '#30a14e', // 12-23小时 - 中深绿色
    '#216e39'  // 24小时及以上 - 最深绿色
  ],
  dark: [
    '#4a5568', // 0 - 适中的灰色（暗色主题）
    '#9be9a8', // 1-5小时 - 浅绿色
    '#40c463', // 6-11小时 - 中浅绿色
    '#30a14e', // 12-23小时 - 中深绿色
    '#216e39'  // 24小时及以上 - 最深绿色
  ]
};

// 共用的获取颜色函数
export function getActivityColor(hours: number, max: number, theme: 'light' | 'dark' = 'light') {
  const colors = ACTIVITY_CALENDAR_COLORS[theme];
  if (hours === 0) return colors[0]; // 0小时根据主题显示不同颜色
  if (hours >= 24) return colors[4]; // 24小时及以上显示最深绿色
  if (hours >= 12) return colors[3]; // 12-23小时
  if (hours >= 6) return colors[2];  // 6-11小时
  return colors[1]; // 1-5小时
}

// 共用的判断是否为今天函数
export function isToday(dateStr: string): boolean {
  if (!dateStr) return false;
  const today = new Date();
  const date = new Date(dateStr);
  return today.getFullYear() === date.getFullYear() &&
         today.getMonth() === date.getMonth() &&
         today.getDate() === date.getDate();
}

// 共用的格式化日期函数
export function formatActivityDate(dateStr: string) {
  const date = new Date(dateStr);
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
}

// 共用的常量
export const WEEK_DAYS = ['一', '二', '三', '四', '五', '六', '日'];
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// 共用的获取月份函数
export function getMonth(dateStr: string) {
  return new Date(dateStr).getMonth();
}

// 新增：计算指定日期所在月份的第一个周一
export function getFirstMondayOfMonth(dateStr: string): string {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = date.getMonth();
  
  // 获取该月第一天
  let current = new Date(year, month, 1);
  
  // 找到该月第一个周一
  while (current.getDay() !== 1) { // 1 = 周一
    current.setDate(current.getDate() + 1);
  }
  
  return current.toISOString().slice(0, 10);
}

// 新增：计算月份标签位置，让每个月对齐该月的第一个周一
export function calculateMonthLabelsByFirstMonday(weeks: any[][], startDate: string, endDate: string): string[] {
  const labels: string[] = [];
  
  // 计算时间范围内的所有月份
  const start = new Date(startDate);
  const end = new Date(endDate);
  const months: { year: number; month: number; firstMonday: string }[] = [];
  
  // 遍历时间范围内的每个月
  let current = new Date(start.getFullYear(), start.getMonth(), 1);
  while (current <= end) {
    const year = current.getFullYear();
    const month = current.getMonth();
    
    // 计算该月的第一个周一
    let firstMonday = new Date(year, month, 1);
    while (firstMonday.getDay() !== 1) { // 1 = 周一
      firstMonday.setDate(firstMonday.getDate() + 1);
    }
    
    // 使用本地时间格式化，避免时区转换问题
    const formatLocalDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    months.push({ 
      year, 
      month, 
      firstMonday: formatLocalDate(firstMonday)
    });
    
    // 移动到下一个月
    current.setMonth(current.getMonth() + 1);
  }
  
  // 为每一周计算月份标签
  for (let weekIndex = 0; weekIndex < weeks.length; weekIndex++) {
    const week = weeks[weekIndex];
    
    // 找到这一周中第一个有日期的格子
    const firstDayWithDate = week.find(day => day.date);
    
    if (firstDayWithDate && firstDayWithDate.date) {
      const currentDate = firstDayWithDate.date;
      
      // 检查是否是某个月的第一个周一
      const matchingMonth = months.find(m => m.firstMonday === currentDate);
      
      if (matchingMonth) {
        // 这是某个月的第一个周一，显示月份标签
        labels.push(MONTHS[matchingMonth.month]);
      } else {
        // 不是第一个周一，显示空白
        labels.push('');
      }
    } else {
      // 这一周没有日期，显示空白
      labels.push('');
    }
  }
  
  return labels;
}

// 重新设计的按周分组函数
export function groupByWeekMondayStart(data: any[], start: string, end: string) {
  // 1. 补齐开头空白（如首日不是周一）
  const firstDay = new Date(start);
  const firstWeekDay = (firstDay.getDay() + 6) % 7; // 0=周一
  let days: any[] = [];
  for (let i = 0; i < firstWeekDay; i++) {
    days.push({ date: '', hours: 0 });
  }
  days = days.concat(data);
  // 2. 补齐结尾空白
  const total = days.length;
  const remain = total % 7 === 0 ? 0 : 7 - (total % 7);
  for (let i = 0; i < remain; i++) {
    days.push({ date: '', hours: 0 });
  }
  // 3. 分组
  const weeks: any[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
}

// 共用的日历格子样式生成函数
export function getCalendarDayStyle(
  day: any, 
  max: number, 
  isDarkTheme: boolean, 
  size: number = 12,
  borderRadius: number = 2
) {
  return {
    width: size,
    height: size,
    background: getActivityColor(day.hours, max, isDarkTheme ? 'dark' : 'light'),
    borderRadius,
    margin: 0,
    boxSizing: 'border-box' as const,
    cursor: day.date ? 'pointer' : 'default',
    userSelect: 'none' as const,
    WebkitUserSelect: 'none' as const,
    touchAction: 'manipulation' as const,
    // 移动端优化
    minWidth: `${size}px`,
    minHeight: `${size}px`,
    transition: 'transform 0.1s ease, box-shadow 0.1s ease',
    WebkitTapHighlightColor: 'transparent',
    // 为空白格子添加透明边框
    border: day.hours === 0 ? `1px solid transparent` : 'none',
  };
}

interface ActivityDay {
  date: string; // YYYY-MM-DD
  hours: number;
}

interface ActivityCalendarProps {
  start: string; // YYYY-MM-DD
  end: string;   // YYYY-MM-DD
  authenticatedFetch?: (url: string, options?: RequestInit) => Promise<Response>;
  currentTheme?: string;
}

const ActivityCalendar: React.FC<ActivityCalendarProps> = ({ start, end, authenticatedFetch, currentTheme = 'modern' }) => {
  const [max, setMax] = useState(1);
  const [weeks, setWeeks] = useState<ActivityDay[][]>([]);
  const [monthLabels, setMonthLabels] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fetchingRef = useRef(false); // 防止重复请求
  const [isMobile, setIsMobile] = useState(false);
  
  // 新增：toast相关状态
  const [toast, setToast] = useState<string | null>(null);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 检测是否为暗色主题
  const isDarkTheme = currentTheme === 'dark';

  // 新增：显示toast的通用函数
  const showToast = useCallback((message: string, duration: number = 3000) => {
    // 清除之前的定时器
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    
    // 设置新的toast
    setToast(message);
    
    // 设置自动隐藏定时器
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, duration);
  }, []);

  // 新增：清除toast的函数
  const clearToast = useCallback(() => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    setToast(null);
  }, []);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  // 检测是否为移动端
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /mobile|android|iphone|ipad|phone|blackberry|opera mini|windows phone/i.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 处理格子点击/触摸
  const handleDayClick = (day: ActivityDay, event: React.MouseEvent | React.TouchEvent) => {
    console.log('=== 点击事件触发 ===');
    console.log('事件类型:', event.type);
    console.log('点击的日期:', day.date);
    console.log('点击的时长:', day.hours);
    
    // 移除 preventDefault() 以避免被动事件监听器错误
    // 只使用 stopPropagation() 阻止事件冒泡
    event.stopPropagation();
    
    if (day.date) {
      console.log('显示toast提示');
      
      // 显示toast提示工作时长
      showToast(`${formatActivityDate(day.date)} - 工作时长: ${day.hours}小时`, 3000);
    } else {
      console.log('没有日期，不显示提示');
    }
  };

  // 使用新的月份标签计算函数，让每个月对齐该月的第一个周一
  const calculateMonthLabels = (weeks: ActivityDay[][]) => {
    return calculateMonthLabelsByFirstMonday(weeks, start, end);
  };

  useEffect(() => {
    const fetchData = async () => {
      console.log('=== 开始获取日历数据 ===');
      console.log('开始日期:', start);
      console.log('结束日期:', end);
      
      // 防止重复请求
      if (fetchingRef.current) {
        console.log('正在获取中，跳过重复请求');
        return;
      }
      fetchingRef.current = true;
      
      try {
        let resp;
        if (authenticatedFetch) {
          resp = await authenticatedFetch(`/api/me/activity_calendar?start=${start}&end=${end}`);
        } else {
          resp = await getActivityCalendar(start, end);
        }
        const res = resp.json ? await resp.json() : resp;
        console.log('获取到的原始数据:', res);
        console.log('数据条数:', res.length);
        
        setMax(Math.max(1, ...res.map((d: ActivityDay) => d.hours)));
        const weekArr = groupByWeekMondayStart(res, start, end);
        console.log('处理后的周数据:', weekArr);
        console.log('周数:', weekArr.length);
        
        setWeeks(weekArr);
        
        // 使用重新设计的月份标签计算函数
        const labels = calculateMonthLabels(weekArr);
        setMonthLabels(labels);
        console.log('计算出的月份标签:', labels);
        
      } catch {
        setWeeks([]);
        setMonthLabels([]);
      } finally {
        fetchingRef.current = false;
      }
    };
    fetchData();
  }, [start, end, authenticatedFetch]);

  // 初始时自动滚动到最右侧
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [weeks, monthLabels]);

  console.log('=== 渲染日历组件 ===');
  console.log('weeks 数据:', weeks);
  console.log('isMobile:', isMobile);
  console.log('屏幕宽度:', window.innerWidth);
  
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'flex-start', 
      padding: isMobile ? 12 : 8,
      // 移动端优化
      touchAction: 'pan-x',
      WebkitOverflowScrolling: 'touch'
    }}>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        {/* 左侧星期标签，显示完整的周几 */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          marginRight: isMobile ? 6 : 4, 
          paddingTop: isMobile ? 17 : 13,
          // 移动端固定定位，防止滚动时移动
          position: 'sticky',
          left: 0,
          zIndex: 1,
          background: 'transparent'
        }}>
          {WEEK_DAYS.map((d, i) => (
            <div
              key={i}
                                style={{
                    height: isMobile ? 16 : 13, // 调整到平均值字高
                    fontSize: isMobile ? '14px' : 'clamp(10px, 2.5vw, 12px)', // 调整到平均值字体大小
                    color: '#888',
                    lineHeight: isMobile ? '16px' : '13px', // 调整到平均值行高
                    textAlign: 'right',
                    width: isMobile ? 20 : 'clamp(14px, 3.2vw, 18px)', // 调整到平均值宽度
                    boxSizing: 'border-box',
                    marginTop: i === 0 ? 0 : (isMobile ? 3 : 2), // 调整到平均值间距
                    // 移动端优化
                    WebkitTextSizeAdjust: '100%',
                    textSizeAdjust: '100%',
                    fontWeight: isMobile ? '500' : 'normal' // 移动端字体加粗
                  }}
            >
              {d}
            </div>
          ))}
        </div>
        {/* 主体内容区域 - 包含月份标签和格子，一起滚动 */}
        <div
          ref={scrollRef}
          style={{ 
            overflowX: 'auto', 
            overflowY: 'hidden',
            maxWidth: isMobile ? 'calc(100vw - 100px)' : 'calc(100vw - 80px)', // 移动端调整最大宽度
            WebkitOverflowScrolling: 'touch'
          }}>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 'max-content' }}>
            {/* 顶部月份标签 */}
            <div style={{ display: 'flex', flexDirection: 'row', marginBottom: isMobile ? 4 : 2 }}>
              {monthLabels.map((label, idx) => (
                <div key={idx} style={{ 
                  width: isMobile ? 18 : 'clamp(14px, 3.5vw, 18px)', // 格子宽度 + gap，确保完美对齐
                  textAlign: 'center', 
                  fontSize: isMobile ? '14px' : 'clamp(10px, 2.5vw, 12px)', // 移动端使用更大字体
                  color: '#888',
                  // 移动端优化
                  WebkitTextSizeAdjust: '100%',
                  textSizeAdjust: '100%',
                  fontWeight: isMobile ? '500' : 'normal' // 移动端字体加粗
                }}>{label}</div>
              ))}
            </div>
            {/* 主体格子 */}
            <div style={{ display: 'flex', flexDirection: 'row', gap: isMobile ? 4 : 'clamp(2px, 0.5vw, 4px)' }}>
              {weeks.map((week, colIdx) => (
                <div key={colIdx} style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 4 : 'clamp(2px, 0.5vw, 4px)' }}>
                  {week.map((d, rowIdx) => (
                    <div
                      key={rowIdx}
                      className={isToday(d.date) ? 'calendar-day today' : 'calendar-day'}
                      style={{
                        width: isMobile ? 16 : 'clamp(12px, 3vw, 16px)', // 移动端使用更大尺寸
                        height: isMobile ? 16 : 'clamp(12px, 3vw, 16px)', // 移动端使用更大尺寸
                        background: getActivityColor(d.hours, max, isDarkTheme ? 'dark' : 'light'),
                        borderRadius: isMobile ? 4 : 'clamp(2px, 0.5vw, 4px)', // 移动端使用更大圆角
                        margin: 0,
                        boxSizing: 'border-box',
                        cursor: d.date ? 'pointer' : 'default',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        touchAction: 'manipulation',
                        border: '1px solid transparent', // 添加边框便于调试
                        // 移动端优化
                        minWidth: isMobile ? '16px' : 'clamp(12px, 3vw, 16px)',
                        minHeight: isMobile ? '16px' : 'clamp(12px, 3vw, 16px)',
                        transition: 'transform 0.1s ease, box-shadow 0.1s ease',
                        WebkitTapHighlightColor: 'transparent',
                        // 确保在移动端正确显示
                        flexShrink: 0,
                        flexGrow: 0
                      }}
                      onClick={(e) => {
                        console.log('onClick 触发，日期:', d.date);
                        handleDayClick(d, e);
                      }}
                      onTouchStart={(e) => {
                        console.log('onTouchStart 触发，日期:', d.date);
                        // 添加触摸反馈
                        const target = e.currentTarget as HTMLElement;
                        target.style.transform = 'scale(0.95)';
                        target.style.boxShadow = '0 0 8px rgba(79, 209, 199, 0.3)';
                        
                        // 恢复原状
                        setTimeout(() => {
                          target.style.transform = '';
                          target.style.boxShadow = '';
                        }, 150);
                        
                        handleDayClick(d, e);
                      }}
                      title={d.date ? `${formatActivityDate(d.date)} - ${d.hours}小时` : ''} // 添加title提示
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
       
       {/* Toast提示 */}
       {toast && (
         <div
           style={{
             position: 'fixed',
             top: '50vh',
             left: '50vw',
             transform: 'translate(-50%, -50%)',
             background: 'rgba(51, 51, 51, 0.95)',
             color: '#fff',
             padding: '18px 28px',
             borderRadius: '16px',
             zIndex: 9999,
             fontSize: '16px',
             fontWeight: '500',
             boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
             maxWidth: '90%',
             textAlign: 'center',
             whiteSpace: 'nowrap',
             backdropFilter: 'blur(10px)',
             border: '1px solid rgba(255, 255, 255, 0.1)',
           }}
         >
           {toast}
         </div>
       )}
    </div>
  );
}

export default ActivityCalendar; 